import React, { useState, useRef, useEffect } from 'react';

function AirportSearch({ airports, value, onChange, placeholder, id }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedAirport = value ? airports.find((a) => a.iata === value) : null;

  // When external value is cleared, also clear query
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const filtered = query.length < 1 ? [] : airports.filter((a) => {
    const q = query.toLowerCase();
    return (
      a.iata.toLowerCase().startsWith(q) ||
      a.city.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      a.country.toLowerCase().includes(q)
    );
  }).slice(0, 10);

  const handleSelect = (airport) => {
    onChange(airport.iata);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlighted((h) => Math.max(h - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && filtered.length > 0) {
      handleSelect(filtered[highlighted]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="airport-search" ref={containerRef}>
      {selectedAirport ? (
        <div className="airport-selected">
          <span className="airport-iata">{selectedAirport.iata}</span>
          <span className="airport-selected-label">
            {selectedAirport.city}, {selectedAirport.country}
          </span>
          <button
            type="button"
            className="airport-clear-btn"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            ✕
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          value={query}
          placeholder={placeholder || 'Search city or airport…'}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlighted(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      )}

      {!selectedAirport && open && filtered.length > 0 && (
        <ul className="airport-dropdown">
          {filtered.map((a, i) => (
            <li
              key={a.iata}
              className={`airport-option ${i === highlighted ? 'highlighted' : ''}`}
              onMouseDown={() => handleSelect(a)}
              onMouseEnter={() => setHighlighted(i)}
            >
              <span className="airport-iata">{a.iata}</span>
              <span className="airport-label">
                {a.city}, {a.country} — {a.name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AirportSearch;
